import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { formatDate } from '@angular/common';
import { finalize, tap } from 'rxjs/operators';
import { Page, Pageable } from '../../model/rest/base';
import {
  productTypeLabel,
  subscriptionEventBadgeClass,
  subscriptionEventLabel,
  Subscription,
  SubscriptionEvent,
  SubscriptionProductType,
  SubscriptionStatus,
} from '../../model/v3/subscriptions';
import { ManagerUtils } from '../../services/utils/v3/manager-utils';
import { SubscriptionsV3RestService } from '../../services/rest/v3/subscriptions-rest.service';
import { AuthServiceProvider } from '../../services/col2/auth.service';
import { FunctionalAccessService } from '../../services/functional-access.service';
import { ModalComponent } from '../modal/modal.component';
import { SubscriptionRuleModalComponent } from '../subscription-rule-modal/subscription-rule-modal.component';

/**
 * School subscription list with the renewal-rule editor and the event-history
 * modal; embedded by the admin school tab and the manager subscriptions page.
 */
@Component({
  selector: 'app-school-subscriptions-list',
  templateUrl: './school-subscriptions-list.component.html',
  standalone: false,
})
export class SchoolSubscriptionsListComponent implements OnInit {
  @Input() schoolId: number;
  /**
   * The member-products page lives in the manager area: an admin has to
   * impersonate the school manager to enter it, a manager navigates directly.
   */
  @Input() adminMode = false;

  subscriptionsPage: Page<Subscription> | null = null;
  pageable = new Pageable(0, 10, ['id,DESC']);
  productFilter: SubscriptionProductType | null = null;
  statusFilter: SubscriptionStatus | null = null;

  productTypes = Object.values(SubscriptionProductType);
  statuses = Object.values(SubscriptionStatus);

  historySubscription: Subscription | null = null;
  historyEvents: SubscriptionEvent[] = [];
  historyLoading = false;
  @ViewChild('historyModal', { static: true })
  historyModal: ModalComponent;

  @ViewChild('ruleEditor', { static: true })
  ruleEditor: SubscriptionRuleModalComponent;

  constructor(
    private router: Router,
    private subscriptionsRest: SubscriptionsV3RestService,
    private authProvider: AuthServiceProvider,
    private functionalAccessService: FunctionalAccessService,
  ) {}

  ngOnInit(): void {
    this.loadSubscriptions();
  }

  productLabel(productType: SubscriptionProductType): string {
    return productTypeLabel(productType);
  }

  loadSubscriptions() {
    this.subscriptionsRest
      .listSubscriptions(this.schoolId, {
        productType: this.productFilter,
        status: this.statusFilter,
        pageable: this.pageable,
      })
      .pipe(tap((page) => (this.subscriptionsPage = page)))
      .subscribe();
  }

  filtersChanged() {
    this.pageable.page = 0;
    this.loadSubscriptions();
  }

  switchPage(pageable: Pageable) {
    this.pageable = pageable;
    this.loadSubscriptions();
  }

  renewalRuleLabel(subscription: Subscription): string {
    if (subscription.renewal.renewalMonthsLimit != null) {
      return `${subscription.renewal.renewalMonthsLimit} months`;
    }
    if (subscription.renewal.renewUntilDate) {
      return `until ${formatDate(subscription.renewal.renewUntilDate, 'd/MM/yyyy', 'en')}`;
    }
    return 'until stopped';
  }

  hasMemberProducts(subscription: Subscription): boolean {
    return !!subscription.memberId;
  }

  missingMemberTooltip(subscription: Subscription): string {
    const member =
      subscription.productType === SubscriptionProductType.StudentRegistration
        ? 'student'
        : 'teacher';
    return `This ${member} no longer exists in the school`;
  }

  openMemberProducts(subscription: Subscription) {
    if (!subscription.memberId) {
      return;
    }
    const memberSegment =
      subscription.productType === SubscriptionProductType.StudentRegistration
        ? 'student'
        : 'teacher';
    if (!this.adminMode) {
      this.router.navigate([
        '/school', this.schoolId, memberSegment, subscription.memberId, 'products',
      ]);
      return;
    }
    this.authProvider
      .get()
      .pipe(
        tap((auth) =>
          auth.logInAsManager(
            this.schoolId,
            `${memberSegment}/${subscription.memberId}/products`,
          ),
        ),
        tap(() => this.functionalAccessService.initialize()),
      )
      .subscribe();
  }

  showHistory(subscription: Subscription) {
    this.historySubscription = subscription;
    this.historyEvents = [];
    this.historyLoading = true;
    this.historyModal.show();
    this.subscriptionsRest
      .getSubscriptionEvents(this.schoolId, subscription.id)
      .pipe(
        tap((events) => (this.historyEvents = events)),
        finalize(() => (this.historyLoading = false)),
      )
      .subscribe();
  }

  eventBadgeClass(event: SubscriptionEvent): string {
    return subscriptionEventBadgeClass(event.eventType);
  }

  eventLabel(event: SubscriptionEvent): string {
    return subscriptionEventLabel(event);
  }

  personName(subscription: Subscription): string {
    return ManagerUtils.getName(subscription.person);
  }

  openRuleEditor(subscription: Subscription) {
    this.ruleEditor.open(subscription);
  }

  onRuleSaved(updated: Subscription) {
    const content = this.subscriptionsPage?.content;
    const index = content?.findIndex((row) => row.id === updated.id) ?? -1;
    if (content && index >= 0) {
      content[index] = updated;
    }
  }
}
